import React from 'react';
import { LoadingOverlay as MantineLoadingOverlay, LoadingOverlayProps as MantineLoadingOverlayProps } from '@mantine/core';

/**
 * LoadingOverlay Props
 */
export interface LoadingOverlayProps {
  /**
   * Whether the overlay is visible
   */
  visible: boolean;

  /**
   * Optional message to display below loader
   */
  message?: string;

  /**
   * Z-index for the overlay
   * @default 1000
   */
  zIndex?: number;

  /**
   * Blur amount for the overlay background
   * @default 2
   */
  blur?: number;
}

/**
 * LoadingOverlay - Full-screen loading indicator with optional message
 *
 * Features:
 * - Blocks user interaction during async operations
 * - Semi-transparent backdrop with blur
 * - Optional loading message
 * - Configurable z-index
 * - Uses Mantine's LoadingOverlay component
 *
 * Usage:
 * ```tsx
 * <Box pos="relative">
 *   <LoadingOverlay visible={isLoading} message="Loading data..." />
 *   <YourContent />
 * </Box>
 * ```
 *
 * Note: Parent container must have `position: relative` for overlay to work correctly
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible,
  message,
  zIndex = 1000,
  blur = 2
}): React.ReactElement => {
  const overlayProps: MantineLoadingOverlayProps['overlayProps'] = {
    radius: 'sm',
    blur
  };

  const loaderProps: MantineLoadingOverlayProps['loaderProps'] = message
    ? {
        children: (
          <div style={{ textAlign: 'center', marginTop: 'var(--mantine-spacing-md)' }}>
            {message}
          </div>
        )
      }
    : undefined;

  return (
    <MantineLoadingOverlay
      visible={visible}
      zIndex={zIndex}
      overlayProps={overlayProps}
      loaderProps={loaderProps}
    />
  );
};
